import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Coins, 
  Gift, 
  Users, 
  QrCode, 
  Trophy, 
  ArrowRight,
  CheckCircle2,
  Store,
  Zap,
  Handshake
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const openSignup = () => navigate("/login?tab=register");

  const features = [
    {
      icon: Users,
      title: "Gerencie Parceiros",
      description: "Cadastre pintores, pedreiros, eletricistas e outros profissionais que indicam clientes."
    },
    {
      icon: QrCode,
      title: "QR Code Único",
      description: "Cada parceiro tem seu QR Code para registro rápido de vendas."
    },
    {
      icon: Coins,
      title: "Sistema de Pontos",
      description: "Pontos calculados automaticamente com base em campanhas personalizadas."
    },
    {
      icon: Gift,
      title: "Recompensas Variadas",
      description: "PIX, recarga de celular, brindes, produtos e cupons de desconto."
    },
    {
      icon: Trophy,
      title: "Gamificação",
      description: "Ranking e níveis Bronze, Prata e Ouro para engajar parceiros."
    },
    {
      icon: Zap,
      title: "Campanhas Visuais",
      description: "Editor drag-and-drop para criar regras de pontuação customizadas."
    }
  ];

  const benefits = [
    "Aumente suas vendas com indicações",
    "Fidelize parceiros profissionais",
    "Automatize o programa de incentivos",
    "Integração fácil com seu ERP",
    "Relatórios completos em tempo real"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="font-heading font-bold text-xl sm:text-2xl text-primary">
              PIN Clube
            </h1>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                onClick={() => navigate("/login")}
                className="sm:hidden h-10 rounded-full px-5 font-medium"
                data-testid="login-btn-header-mobile"
              >
                Entrar
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="hidden sm:flex"
                data-testid="login-btn-header"
              >
                Entrar
              </Button>
              <Button
                onClick={openSignup}
                className="btn-primary hidden sm:flex"
                data-testid="get-started-btn"
              >
                Começar agora
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <div className="absolute inset-0 bg-gradient-to-l from-primary/30 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-medium text-sm mb-6">
                <Handshake className="h-4 w-4 shrink-0" />
                Parceiros mais engajados, vendas mais frequentes
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
                Transforme indicações em{" "}
                <span className="text-primary">resultados</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                Plataforma completa para programa de incentivos. 
                Seus parceiros indicam clientes, acumulam pontos e trocam por recompensas incríveis.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={openSignup}
                  className="btn-primary text-lg h-14"
                  data-testid="hero-cta-btn"
                >
                  Criar conta grátis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" })}
                  className="btn-secondary text-lg h-14"
                >
                  Ver como funciona
                </Button>
              </div>
              
              {/* Trust badges */}
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
                  <span>Indicações rastreadas do início ao fim</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
                  <span>Recompensas que motivam novas vendas</span>
                </div>
              </div>
            </div>

            {/* Hero image */}
            <div className="relative animate-fade-in hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBzbWlsaW5nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzczNzkxMzcxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Parceiro satisfeito"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              
              {/* Floating card */}
              <div className="absolute -left-8 bottom-20 bg-white rounded-2xl p-4 shadow-xl animate-slide-up">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
                    <Trophy className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Nível atual</p>
                    <p className="font-heading font-bold text-lg text-slate-900">Ouro</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -right-4 top-20 bg-white rounded-2xl p-4 shadow-xl animate-slide-up stagger-2">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Coins className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pontos ganhos</p>
                    <p className="font-heading font-bold text-lg text-slate-900">+2.500</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="como-funciona" className="py-20 sm:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              Tudo que você precisa em uma plataforma
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Gerencie seu programa de incentivos de forma simples e eficiente
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="stat-card card-hover animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
                Por que escolher o PIN Clube?
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Uma solução completa para lojas de varejo que querem aumentar vendas através de parcerias estratégicas.
              </p>
              
              <ul className="mt-8 space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="h-6 w-6 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-success" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>

              <Button
                size="lg"
                onClick={openSignup}
                className="btn-primary mt-8"
                data-testid="benefits-cta-btn"
              >
                Começar gratuitamente
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="imagem_loja.jpg"
                  alt="Loja de varejo moderna"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              
              {/* Floating stats */}
              <div className="absolute -left-4 -bottom-4 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <Store className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-heading font-bold text-slate-900">+500</p>
                    <p className="text-sm text-slate-500">Lojas ativas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">
            Pronto para transformar seu programa de incentivos?
          </h2>
          <p className="mt-4 text-lg text-blue-100">
            Comece gratuitamente e veja os resultados em semanas.
          </p>
          <Button
            size="lg"
            onClick={openSignup}
            className="mt-8 bg-white text-primary hover:bg-slate-100 rounded-full px-8 py-4 text-lg font-semibold shadow-xl"
            data-testid="final-cta-btn"
          >
            Criar minha conta
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <h3 className="font-heading font-bold text-xl text-white">
              PIN Clube
            </h3>
            <div className="text-center sm:text-right">
              <p className="text-slate-300 text-sm">
                Uma solução UP2 Soluções para programas de incentivo.
              </p>
              <p className="text-slate-500 text-xs mt-1">
                © 2026 UP2 Soluções. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
